import {
  type ClipboardEvent,
  FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { authBackgroundPatternUrl, primaryLogoPngUrl } from "@/config/public-assets";
import { authMessages } from "@/features/auth/messages";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import type { VerifyOtpLocationState } from "@/features/auth/types/loginFlow";
import {
  AdminOtpVerifyError,
  verifyAdminLogin,
  resendAdminLogin,
} from "@/features/auth/api/adminAuthApi";
import { setAdminAccessToken } from "@/features/auth/session/adminAccessToken";
import { setAdminSessionEmail } from "@/features/auth/session/adminSessionEmail";
import { maskEmailForOtpDisplay } from "@/features/auth/utils/maskEmail";
import styles from "./VerifyOtpPage.module.css";

const DEFAULT_OTP_EXPIRY_SECONDS = 10 * 60;
const RESEND_COOLDOWN_SECONDS = 60;

export default function VerifyOtpPage() {
  useDocumentTitle(authMessages.documentTitle.verifyOtp);
  const { brand, verifyOtp: verifyMsg } = authMessages;

  const location = useLocation()
  const navigate = useNavigate()
  const loginState = location.state as VerifyOtpLocationState | null;
  const email = loginState?.email?.trim() ?? "";
  const initialOtpDisplayTarget =
    loginState?.otpSentTo?.trim() ||
    maskEmailForOtpDisplay(email);
  const initialExpirySeconds =
    loginState?.otpExpiresInSeconds &&
    loginState.otpExpiresInSeconds > 0
      ? loginState.otpExpiresInSeconds
      : DEFAULT_OTP_EXPIRY_SECONDS;
  const expiryMinutes = Math.max(1, Math.round(initialExpirySeconds / 60));

  const [otpRaw, setOtpRaw] = useState("");
  const [otpDisplayTarget, setOtpDisplayTarget] = useState(initialOtpDisplayTarget);
  const [secondsLeft, setSecondsLeft] = useState(initialExpirySeconds);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendsRemaining, setResendsRemaining] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState<number | null>(
    null,
  );
  const [showResendHelp, setShowResendHelp] = useState(false);
  const resendHelpRef = useRef<HTMLDivElement | null>(null);
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>(
    Array.from({ length: 6 }, () => null),
  )

  useEffect(() => {
    if (secondsLeft <= 0) {
      return
    }
    const id = window.setInterval(() => {
      setSecondsLeft((t) => (t <= 1 ? 0 : t - 1))
    }, 1000)
    return () => window.clearInterval(id)
  }, [secondsLeft])

  useEffect(() => {
    if (resendCooldown <= 0) {
      return
    }
    const id = window.setInterval(() => {
      setResendCooldown((t) => (t <= 1 ? 0 : t - 1))
    }, 1000)
    return () => window.clearInterval(id)
  }, [resendCooldown])

  useEffect(() => {
    if (!showResendHelp) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      if (
        resendHelpRef.current &&
        !resendHelpRef.current.contains(event.target as Node)
      ) {
        setShowResendHelp(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setShowResendHelp(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showResendHelp]);

  if (!email) {
    return <Navigate to="/login" replace />
  }

  function setFromDigits(digits: string) {
    setOtpRaw(digits.replace(/\D/g, "").slice(0, 6))
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault()
    const pasted = e.clipboardData.getData("text")
    setFromDigits(pasted)
  }

  function handleBoxChange(index: number, value: string) {
    const nextDigit = value.replace(/\D/g, "").slice(-1)
    if (!nextDigit) {
      // Treat as delete at this position. Keep the left portion and ignore later digits.
      setOtpRaw((prev) => prev.slice(0, index) + prev.slice(index + 1))
      return
    }

    setOtpRaw((prev) => {
      const before = prev.slice(0, index)
      const after = prev.length > index ? prev.slice(index + 1) : ""
      return `${before}${nextDigit}${after}`.slice(0, 6)
    })

    if (index < 5) {
      // Best-effort focus forward (wireframe behavior).
      const nextEl = otpInputRefs.current[index + 1]
      if (nextEl) {
        nextEl.focus()
      }
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (otpRaw.length !== 6 || secondsLeft <= 0 || isSubmitting) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const result = await verifyAdminLogin({
        code: otpRaw,
        remember_device: false,
      });
      setAdminAccessToken(result.access_token, result.expires_in);
      setAdminSessionEmail(result.user.email || email);
      navigate("/customers", { replace: true });
    } catch (err) {
      if (err instanceof AdminOtpVerifyError) {
        if (err.errorCode === "session_voided") {
          navigate("/login", { replace: true, state: { sessionExpired: true } });
          return;
        }

        setError(err.message);

        if (
          err.errorCode === "invalid_otp" ||
          err.attemptsRemaining !== null
        ) {
          setOtpAttemptsRemaining(err.attemptsRemaining);
        }

        return;
      }

      setError(
        err instanceof Error
          ? err.message
          : verifyMsg.verificationFailed,
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || isResending || resendsRemaining === 0) {
      return;
    }

    setError(null);
    setIsResending(true);

    try {
      const result = await resendAdminLogin();
      setOtpDisplayTarget(result.otp_sent_to);
      setResendsRemaining(result.resends_remaining);
      setOtpRaw("");
      setSecondsLeft(initialExpirySeconds);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setOtpAttemptsRemaining(null);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : verifyMsg.resendFailed;

      if (/login session|start login/i.test(message)) {
        navigate("/login", { replace: true, state: { sessionExpired: true } });
        return;
      }

      setError(message);
    } finally {
      setIsResending(false);
    }
  }

  const canResend = resendCooldown <= 0 && !isResending && resendsRemaining !== 0;

  return (
    <div
      className={styles.page}
      style={{ backgroundImage: `url(${authBackgroundPatternUrl})` }}
      data-testid="verify-otp-page"
    >
      <main
        className={styles.card}
        aria-labelledby="verify-heading"
        data-testid="verify-otp-card"
      >
        <div className={styles.brandH} data-testid="verify-otp-brand">
          <img
            src={primaryLogoPngUrl}
            alt={brand.name}
            className={styles.brandLogoFull}
            decoding="async"
            referrerPolicy="no-referrer"
          />
          <span className={styles.brandAdmin}>{brand.adminPortal}</span>
        </div>

        <h1 id="verify-heading" className={styles.title} data-testid="verify-otp-title">
          {verifyMsg.title}
        </h1>
        <p className={styles.subtitle} data-testid="verify-otp-subtitle">
          {verifyMsg.subtitleBeforeEmail}{" "}
          <strong data-testid="verify-otp-masked-email">{otpDisplayTarget}</strong>
          {verifyMsg.subtitleAfterEmail}
          {verifyMsg.codeValidity(expiryMinutes)}
        </p>

        <div className={styles.secBadge} data-testid="verify-otp-step-banner">
          {verifyMsg.stepBanner}
        </div>

        {error ? (
          <p className={styles.formError} role="alert" data-testid="verify-otp-error">
            {error}
          </p>
        ) : null}

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          data-testid="verify-otp-form"
        >
          <div className={styles.otpGrid} data-testid="verify-otp-code-grid">
            {Array.from({ length: 6 }).map((_, index) => {
              const digit = otpRaw[index] ?? ""
              return (
                <input
                  key={index}
                  ref={(el) => {
                    otpInputRefs.current[index] = el
                  }}
                  className={`${styles.otpBox} ${digit ? styles.otpBoxFilled : ""}`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={1}
                  value={digit}
                  onChange={(ev) => handleBoxChange(index, ev.target.value)}
                  onPaste={handlePaste}
                  aria-label={verifyMsg.digitAriaLabel(index + 1)}
                  data-testid={`verify-otp-digit-${index}`}
                />
              )
            })}
          </div>

          <button
            type="submit"
            className={styles.submit}
            disabled={otpRaw.length !== 6 || secondsLeft <= 0 || isSubmitting}
            data-testid="verify-otp-submit-button"
          >
            {isSubmitting ? verifyMsg.submitting : verifyMsg.submit}
          </button>
        </form>

        <div className={styles.resendRow} data-testid="verify-otp-resend-row">
          <div className={styles.resendPromptWrap}>
            <span className={styles.resendPrompt}>{verifyMsg.resendPrompt}</span>
            <div className={styles.resendHelpWrap} ref={resendHelpRef}>
              <button
                type="button"
                className={styles.resendHelpBtn}
                onClick={() => setShowResendHelp((open) => !open)}
                aria-expanded={showResendHelp}
                aria-label={verifyMsg.resendHelpAriaLabel}
                data-testid="verify-otp-resend-help"
              >
                <svg
                  className={styles.resendHelpIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
                  <path
                    d="M9.75 9.25a2.25 2.25 0 0 1 4.1 1.15c0 1.35-2.1 1.6-2.1 3.1"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="16.25" r="0.9" fill="currentColor" />
                </svg>
              </button>
              {showResendHelp ? (
                <div
                  className={styles.resendHelpPopover}
                  role="tooltip"
                  data-testid="verify-otp-resend-help-popover"
                >
                  {verifyMsg.resendHelp}
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className={styles.resendLink}
            onClick={() => void handleResend()}
            disabled={!canResend}
            data-testid="verify-otp-resend-button"
          >
            {isResending
              ? verifyMsg.resending
              : resendsRemaining === 0
                ? verifyMsg.resendExhausted
                : resendCooldown > 0
                  ? verifyMsg.resendCooldown(resendCooldown)
                  : verifyMsg.resend}
          </button>
        </div>

        {otpAttemptsRemaining !== null ? (
          <div
            className={styles.troubleshootingPanel}
            data-testid="verify-otp-troubleshooting-panel"
          >
            <strong>
              {verifyMsg.troubleshootingWithAttempts(otpAttemptsRemaining)}
            </strong>
          </div>
        ) : null}

        <div className={styles.backWrap} data-testid="verify-otp-back-wrap">
          <Link
            to="/login"
            replace
            className={styles.backLink}
            data-testid="verify-otp-back-to-signin-link"
          >
            {verifyMsg.backToSignIn}
          </Link>
        </div>
      </main>
    </div>
  )
}
